/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        hooks/useOfflineSync.ts
 * @description Hook React pour gerer l'etat online/offline et la synchronisation
 *              Fournit un moyen simple de connaitre l'etat de connexion
 *              et le nombre d'actions en attente
 *
 * @version     2.4
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { syncService, type SyncResult } from '@/services/syncService';

// =============================================================================
// TYPES
// =============================================================================

export interface UseOfflineSyncReturn {
  /** Whether the device is currently online */
  isOnline: boolean;
  /** Number of pending actions waiting to be synced */
  pendingCount: number;
  /** Whether a sync is currently in progress */
  isSyncing: boolean;
  /** Last sync result */
  lastSyncResult: SyncResult | null;
  /** Last sync timestamp */
  lastSyncTime: Date | null;
  /** Manually trigger a sync */
  sync: () => Promise<SyncResult>;
  /** Force refresh pending count */
  refreshPendingCount: () => Promise<void>;
}

// =============================================================================
// HOOK
// =============================================================================

export function useOfflineSync(): UseOfflineSyncReturn {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // Ref to track if component is mounted
  const mountedRef = useRef(true);

  // Refresh pending count
  const refreshPendingCount = useCallback(async () => {
    try {
      const count = await syncService.getPendingCount();
      if (mountedRef.current) {
        setPendingCount(count);
      }
    } catch (error) {
      console.error('Failed to get pending count:', error);
    }
  }, []);

  // Manual sync function
  const sync = useCallback(async (): Promise<SyncResult> => {
    if (isSyncing || !isOnline) {
      return { success: false, synced: 0, failed: 0, errors: [] };
    }

    setIsSyncing(true);
    try {
      const result = await syncService.syncPendingActions();
      if (mountedRef.current) {
        setLastSyncResult(result);
        setLastSyncTime(new Date());
        await refreshPendingCount();
      }
      return result;
    } finally {
      if (mountedRef.current) {
        setIsSyncing(false);
      }
    }
  }, [isSyncing, isOnline, refreshPendingCount]);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync when coming back online
      sync().catch(console.error);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [sync]);

  // Listen to sync service for updates
  useEffect(() => {
    const unsubscribe = syncService.addSyncListener((result) => {
      if (mountedRef.current) {
        setLastSyncResult(result);
        setLastSyncTime(new Date());
        refreshPendingCount();
      }
    });

    return unsubscribe;
  }, [refreshPendingCount]);

  // Initial pending count
  useEffect(() => {
    refreshPendingCount();
  }, [refreshPendingCount]);

  // Periodic pending count refresh
  useEffect(() => {
    const interval = setInterval(() => {
      refreshPendingCount();
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [refreshPendingCount]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncResult,
    lastSyncTime,
    sync,
    refreshPendingCount,
  };
}

// =============================================================================
// ADDITIONAL HOOKS
// =============================================================================

/**
 * Simple hook that just returns online status
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

/**
 * Hook for GPS geolocation
 */
export function useGeolocation(): {
  position: GeolocationPosition | null;
  error: GeolocationPositionError | null;
  loading: boolean;
  refresh: () => void;
} {
  const [position, setPosition] = useState<GeolocationPosition | null>(null);
  const [error, setError] = useState<GeolocationPositionError | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(() => {
    if (!navigator.geolocation) {
      setError({
        code: 2,
        message: 'Geolocation not supported',
        PERMISSION_DENIED: 1,
        POSITION_UNAVAILABLE: 2,
        TIMEOUT: 3,
      } as GeolocationPositionError);
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition(pos);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000, // Cache for 1 minute
      }
    );
  }, []);

  // Get position on mount
  useEffect(() => {
    refresh();
  }, [refresh]);

  return { position, error, loading, refresh };
}

export default useOfflineSync;
